import classNames from 'classnames';
import memoize from 'memoize-one';
import { forwardRef, Fragment, useMemo, useReducer, useState } from 'react';
import { Link } from 'react-router';

import { CRAWLER_RENDERING } from 'my-core/analytics/analytics-constants';
import { appBarNavLinkClicked } from 'my-core/gtm-events';
import { isTouchEnabled } from 'my-core/user-agent-utils';

import ButtonBase from '@mui/material/ButtonBase';
import Collapse, { collapseClasses } from '@mui/material/Collapse';
import Grow from '@mui/material/Grow';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import { listItemSecondaryActionClasses } from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';

import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const rootWrapperCss = { position: 'relative' };
const inlineStyle = { position: 'static', width: 'auto', maxWidth: 'none', boxShadow: 'none' };

export default function AppBarNavLink(props) {
  const { active: activeProp, classes, fetching, icon, items, label, location, path, subLabel, underline } = props;
  const canHover = !isTouchEnabled();

  const matchPath = useMemo(() => createLocationPathMatcher(location), [location]);

  const itemMatches = useMemo(
    () =>
      items?.map(
        i =>
          i && {
            skip: i.skipMatching,
            match: matchPath(i.path),
            sub_matches: i.items?.map(i => ({ skip: i.skipMatching, match: matchPath(i.path) })),
          },
      ),
    [items, matchPath],
  );
  const rootActive = activeProp || matchPath(path);
  const active = useMemo(
    () =>
      rootActive || itemMatches?.some(i => !i?.skip && (i?.match || i.sub_matches?.some(si => !si.skip && si.match))),
    [rootActive, itemMatches],
  );

  const [buttonOpen, setButtonOpen] = useState(false);

  // The dropdown's list, always open and in the page flow, for a menu that already is a popover.
  if (props.inline)
    return (
      <DropDownItems
        itemMatches={itemMatches}
        items={items}
        onChangeButtonOpen={props.onChangeOpen}
        style={inlineStyle}
      />
    );

  if (!items?.length)
    return (
      <ButtonBase
        className={classNames(classes.rootWrapper, classes.root, {
          [classes.root_active]: active,
          [classes.root_underline]: underline,
        })}
        component={Link}
        css={rootWrapperCss}
        nativeButton={false}
        onClick={() => appBarNavLinkClicked(label)}
        to={path}
      >
        {label}
      </ButtonBase>
    );

  return (
    <div className={classes.rootWrapper} css={rootWrapperCss} onMouseLeave={() => setButtonOpen(false)}>
      <ButtonBase
        className={classNames(classes.root, {
          [classes.root_open]: buttonOpen,
          [classes.root_active]: active,
          [classes.root_underline]: underline,
        })}
        component={canHover && path ? Link : undefined}
        nativeButton={canHover && path ? false : undefined}
        onClick={() => {
          setButtonOpen(!buttonOpen);
          appBarNavLinkClicked(label);
        }}
        onMouseEnter={canHover ? () => setButtonOpen(true) : undefined}
        to={canHover ? path : undefined}
      >
        {label}
        <FontAwesomeIcon icon={faChevronDown} size="xs" />
      </ButtonBase>
      <Grow in={buttonOpen} mountOnEnter={!CRAWLER_RENDERING}>
        <DropDownItems
          fetching={fetching}
          itemMatches={itemMatches}
          items={items}
          onChangeButtonOpen={setButtonOpen}
          rootActive={rootActive}
          rootIcon={icon}
          rootLabel={subLabel || label}
          rootPath={path}
        />
      </Grow>
    </div>
  );
}

const DropDownItems = forwardRef(function DropDownItems(
  { fetching, itemMatches, items, onChangeButtonOpen, rootActive, rootIcon, rootLabel, rootPath, style },
  forwardedRef,
) {
  return (
    <Paper
      css={({ breakpoints, palette, transitions }) => ({
        position: 'absolute',
        width: 'max-content',
        maxWidth: '95vw',
        transformOrigin: 'top',
        left: 0,
        top: '100%',
        [breakpoints.down(400)]: { left: -50 },

        '.listItem': {
          color: palette.text.primary,
          '&:hover, &.listItem_active': { backgroundColor: palette.action.hover },
          [`& > .${listItemSecondaryActionClasses.root}`]: {
            right: 0,
            height: '100%',
            width: 48,
            '& > button': { height: '100%', width: '100%' },
            svg: {
              transition: transitions.create(['transform'], { duration: transitions.duration.shorter }),
            },
          },
        },
        [`.${collapseClasses.root} .listItem > a`]: { paddingLeft: 56 },
        '.listItem_active': { '&, & svg': { color: palette.primary.main } },
        '.listItem_open': { [`& > .${listItemSecondaryActionClasses.root} svg`]: { transform: 'rotate(180deg)' } },
        '.listItemIcon': { minWidth: 40 },
      })}
      elevation={4}
      ref={forwardedRef}
      style={style}
    >
      <List component="nav">
        {!!rootPath && (
          <NavListItem
            active={rootActive}
            icon={!!items[0].icon && rootIcon}
            label={rootLabel}
            onChangeButtonOpen={onChangeButtonOpen}
            path={rootPath}
          />
        )}
        {items.map((item, idx) => {
          if (fetching) {
            return (
              <ListItem key={idx}>
                <ListItemText primary={<Skeleton width={80 + Math.floor(Math.random() * 80)} />} />
              </ListItem>
            );
          }

          if (item.items?.length) {
            return (
              <NavListItemWithNested
                key={idx}
                active={itemMatches[idx].match || itemMatches[idx].sub_matches.some(m => m.match)}
                icon={item.icon}
                items={item.items}
                label={item.label}
                onChangeButtonOpen={onChangeButtonOpen}
                path={item.path}
                rootActive={itemMatches[idx].match}
                subLabel={item.subLabel}
                subMatches={itemMatches[idx].sub_matches}
              />
            );
          }
          return (
            <NavListItem
              key={idx}
              active={itemMatches[idx]?.match}
              icon={item.icon}
              label={item.label}
              onChangeButtonOpen={onChangeButtonOpen}
              path={item.path}
            />
          );
        })}
      </List>
    </Paper>
  );
});

function NavListItemWithNested({ items, onChangeButtonOpen, path, rootActive, subLabel, subMatches, ...rest }) {
  const [expanded, toggleExpanded] = useReducer(prev => !prev, false);
  return (
    <Fragment>
      <NavListItem
        className={classNames({ listItem_open: expanded })}
        onChangeButtonOpen={onChangeButtonOpen}
        onToggleExpanded={toggleExpanded}
        path={path}
        secondaryAction={
          <ButtonBase onClick={toggleExpanded}>
            <FontAwesomeIcon icon={faChevronDown} />
          </ButtonBase>
        }
        {...rest}
      />
      <Collapse in={expanded}>
        <NavListItem
          {...rest}
          active={rootActive}
          icon={undefined}
          label={subLabel}
          onChangeButtonOpen={onChangeButtonOpen}
          path={path}
        />
        {items.map((item, jdx) => (
          <NavListItem
            key={jdx}
            active={subMatches[jdx]?.match}
            icon={item.icon}
            label={item.label}
            onChangeButtonOpen={onChangeButtonOpen}
            path={item.path}
          />
        ))}
      </Collapse>
    </Fragment>
  );
}

function NavListItem({ active, className, icon, label, onChangeButtonOpen, onToggleExpanded, path, ...rest }) {
  return (
    <ListItem className={classNames(className, 'listItem', { listItem_active: active })} disablePadding {...rest}>
      <ListItemButton
        component={path ? Link : undefined}
        nativeButton={path ? false : undefined}
        onClick={() => {
          appBarNavLinkClicked(label);
          path ? onChangeButtonOpen(false) : onToggleExpanded?.();
        }}
        to={path}
      >
        {icon && (
          <ListItemIcon className="listItemIcon">
            <FontAwesomeIcon icon={icon} size="lg" />
          </ListItemIcon>
        )}
        <ListItemText primary={label} />
      </ListItemButton>
    </ListItem>
  );
}

const createLocationPathMatcher = memoize(location => path => {
  if (!path) return false;
  const [pathname, search] = typeof path === 'string' ? path.split('?') : [path.pathname, path.search?.substring(1)];
  return location.pathname === pathname && (!search || location.search.includes(search));
});
